import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  Alert,
  Stack,
} from '@mui/material';
import { useMutation, useQuery } from '@apollo/client';
import { GET_CHANGES_SINCE_LAST_PUBLISH, PUBLISH_GUIDE, GUIDE_TIPTAP_DOCUMENT_STATUS } from './guide.graphql';
import LoadingSpinner from '../LoadingSpinner';
import { PublishType, GuideStatus } from '../__generated__/graphql';
import { Guide } from './Details';
import { Modal } from '../dialogs/useModal';
import { Edit } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { useAuth } from 'oidc-react';
import { GET_USER } from '../home/home.graphql';

export function PublishGuideDialog({ modal }: { modal: Modal<Guide, unknown> }) {
  const guide = modal.data;
  const { data: tiptapDocumentStatus } = useQuery(GUIDE_TIPTAP_DOCUMENT_STATUS, {
    variables: { documentName: guide.tiptapDocument?.documentName },
    fetchPolicy: 'network-only',
  });
  const isNew = guide.status === GuideStatus.Planned;
  const noOngoingRevision = !isNew && guide.ongoingRevision == null;
  const [forceMajor, setForceMajor] = useState(guide.versions!.length === 0);
  const [publishType, setPublishType] = useState<PublishType | undefined>(forceMajor ? PublishType.Major : undefined);
  const [publishGuide, { loading }] = useMutation(PUBLISH_GUIDE, {
    variables: { input: { id: guide.id, releaseNotes: guide.releaseNotes, type: publishType ?? PublishType.Major } },
  });
  const lastRelease = guide.versions!.length > 0 ? [...guide.versions!].reverse()[0] : undefined;
  const isPublisher = useIsPublisher();

  const canPublish = isPublisher && publishType !== undefined;

  const handleClose = () => {
    modal.close();
  };

  const handleOk = async () => {
    await publishGuide();
    handleClose();
  };

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newType = event.target.value as PublishType;
    setPublishType(newType);
  };

  const getNextVersionNumber = (): string => {
    if (!lastRelease) return '1.0';

    var major = lastRelease.major;
    var minor = lastRelease.minor;

    if (publishType === PublishType.Major) {
      major++;
      minor = 0;
    } else if (publishType === PublishType.Minor) {
      minor++;
    }

    return `${major}.${minor}`;
  };

  const setMajorChange = () => {
    if (publishType !== PublishType.Major) setPublishType(PublishType.Major);
    if (!forceMajor) setForceMajor(true);
  };

  const versionNumber = getNextVersionNumber();

  return (
    <Dialog sx={{ minHeight: '25vh' }} fullWidth={true} maxWidth={'md'} open={true} onClose={handleClose} aria-labelledby="form-dialog-title">
      <DialogTitle id="form-dialog-title">Publiser anvisning</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <FormControl component="fieldset">
            <FormLabel>Type revisjon (velg)</FormLabel>
            <RadioGroup aria-label="type" name="type" value={publishType ?? 'Unset'} onChange={handleTypeChange}>
              <FormControlLabel
                value={PublishType.Major}
                disabled={noOngoingRevision}
                control={<Radio size="small" color="primary" />}
                label="Fullstendig revisjon"
              />
              <FormControlLabel
                value={PublishType.Minor}
                disabled={forceMajor || noOngoingRevision}
                control={<Radio size="small" color="primary" />}
                label="Begrenset revisjon"
              />
              <FormControlLabel value={PublishType.Bugfix} disabled={forceMajor} control={<Radio size="small" color="primary" />} label="Utbedring" />
            </RadioGroup>
            {noOngoingRevision && (
              <Alert severity="info">
                Fullstendig revisjon og Begrenset revisjon krever en pågående revisjon. Start en revisjon for å låse opp disse alternativene.
              </Alert>
            )}
            {forceMajor && guide.versions!.length === 0 && (
              <Alert severity="info">Første publisering må være en Fullstendig revisjon.</Alert>
            )}
            {forceMajor && guide.versions!.length > 0 && (
              <Alert severity="warning">Det er registrert vesentlige endringer siden siste publisering. Kun Fullstendig revisjon er tilgjengelig.</Alert>
            )}
          </FormControl>
          {!isNew && <ListChanges id={guide.id} onMajorChanges={setMajorChange} />}
          <div>
            <Typography color="textSecondary" display="inline">
              Nytt versjonsnummer (auto-generert)
            </Typography>{' '}
            <Typography>{publishType ? versionNumber : ''}</Typography>
          </div>
          <div>
            <Typography color="textSecondary">Anvisningstittel (kopi)</Typography>
            <Typography fontStyle="italic">{guide.docTitle}</Typography>
          </div>
          <div>
            <Typography color="textSecondary">Hva er endret (synlig for kunde - kopi)</Typography>
            <Typography whiteSpace="pre-wrap" fontStyle="italic">
              {guide.releaseNotes}
            </Typography>
          </div>
          {tiptapDocumentStatus?.guideTiptapDocumentStatus?.isDirty && (
            <Alert
              severity="warning"
              action={
                <Button size="small" color="inherit" component={Link} to={`/guide-editor/${guide.id}/edit`} startIcon={<Edit />}>
                  Endre i CMS
                </Button>
              }>
              CMSet innholder ulagrede endringer
            </Alert>
          )}
          {(tiptapDocumentStatus?.guideTiptapDocumentStatus?.unresolvedComments ?? 0) > 0 && (
            <Alert
              severity="warning"
              action={
                <Button size="small" color="inherit" component={Link} to={`/guide-editor/${guide.id}/edit`} startIcon={<Edit />}>
                  Endre i CMS
                </Button>
              }>
              Det finnes {tiptapDocumentStatus!.guideTiptapDocumentStatus!.unresolvedComments}{' '}
              {tiptapDocumentStatus!.guideTiptapDocumentStatus!.unresolvedComments === 1 ? 'åpen kommentar' : 'åpne kommentarer'}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Avbryt</Button>
        <Button disabled={!canPublish || loading} onClick={handleOk} variant="contained" color="primary">
          Publisér
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ListChanges({ id, onMajorChanges }: { id: string; onMajorChanges(): void }) {
  const { data, loading } = useQuery(GET_CHANGES_SINCE_LAST_PUBLISH, {
    variables: { id },
    fetchPolicy: 'cache-and-network',
  });
  const majorChanges = data && data.guide?.trackingChanges?.some((x) => x?.isMajor);

  if (majorChanges) onMajorChanges();

  return (
    <div>
      <Typography color="textSecondary">Endringer som vil publiseres (auto-generert)</Typography>
      <ul style={{ margin: 0 }}>
        {!data && <LoadingSpinner size={50} />}
        {data &&
          data.guide?.trackingChanges?.map((item, index) => (
            <li key={index}>
              <Typography fontStyle={'italic'}>{item?.description}</Typography>
            </li>
          ))}
      </ul>
    </div>
  );
}

const useIsPublisher = () => {
  const auth = useAuth();
  const { data } = useQuery(GET_USER, {
    variables: { userId: auth?.userData?.profile.sub },
  });
  return data?.users?.[0]?.canPublish ?? false;
};
